import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Play, Pause, Square } from "lucide-react";
import { cases, EMPTY_FIELD } from "@/config";
import { useVoice } from "@/context/VoiceContext";
import { speak, splitSentences } from "@/lib/speech";

const TABS = [
  { key: "problem", label: "The problem" },
  { key: "move", label: "My move" },
  { key: "safeguards", label: "Safeguards" },
  { key: "outcome", label: "Outcome" },
];
const RATES = [0.85, 1, 1.25, 1.5];

const outcomeText = (c) => {
  const parts = [c.outcome || `Outcome ${EMPTY_FIELD.toLowerCase()}.`];
  if (c.outcomeNote) parts.push(c.outcomeNote);
  if (c.stack) {
    const s = c.stack;
    parts.push(`Speech to text: ${s.stt || EMPTY_FIELD}.`, `Language model: ${s.llm || EMPTY_FIELD}.`, `Text to speech: ${s.tts || EMPTY_FIELD}.`, `Response time: ${s.responseTime || EMPTY_FIELD}.`);
  }
  return parts.join(" ");
};

/** Build sentence list with tab ownership so the reader can highlight and switch tabs. */
const buildScript = (c) => {
  const out = [];
  out.push({ tab: -1, text: `${c.title}.` });
  out.push({ tab: -1, text: c.hook });
  TABS.forEach((t, ti) => {
    const body = t.key === "outcome" ? outcomeText(c) : c[t.key];
    out.push({ tab: ti, text: `${t.label}.`, heading: true });
    splitSentences(body).forEach((s) => out.push({ tab: ti, text: s }));
  });
  return out;
};

export default function CaseModal() {
  const { caseIndex, setCaseIndex, openCase, registerPlayer } = useVoice();
  const open = caseIndex !== null;
  const c = open ? cases.items[caseIndex] : null;
  const [tab, setTab] = useState(0);
  const [reading, setReading] = useState("idle"); // idle | playing | paused
  const [current, setCurrent] = useState(-1);
  const [rate, setRate] = useState(1);
  const ctrl = useRef(null);
  const script = useMemo(() => (c ? buildScript(c) : []), [c]);

  const stop = () => { ctrl.current?.stop(); ctrl.current = null; setReading("idle"); setCurrent(-1); };
  const play = () => {
    if (!c) return;
    if (reading === "paused") { ctrl.current?.resume(); setReading("playing"); return; }
    if (reading === "playing") return;
    ctrl.current = speak(script.map((s) => s.text), {
      lang: "en-IN",
      rate,
      onSentence: (i) => { setCurrent(i); if (script[i].tab >= 0) setTab(script[i].tab); },
      onEnd: () => { setReading("idle"); setCurrent(-1); ctrl.current = null; },
      onError: () => { setReading("idle"); setCurrent(-1); ctrl.current = null; },
    });
    setReading("playing");
  };
  const pause = () => { ctrl.current?.pause(); setReading("paused"); };
  const close = () => { stop(); setCaseIndex(null); };

  useEffect(() => registerPlayer("case", { play, stop }));
  useEffect(() => {
    if (!open) return;
    const onListen = () => play();
    window.addEventListener("bca:case-listen", onListen);
    return () => window.removeEventListener("bca:case-listen", onListen);
  });
  useEffect(() => { stop(); setTab(0); }, [caseIndex]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) { document.body.style.overflow = ""; return; }
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") openCase(caseIndex + 1);
      if (e.key === "ArrowLeft") openCase(caseIndex - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, caseIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeRate = (r) => { setRate(r); ctrl.current?.setRate(r); };

  const renderTab = (ti) => {
    const lines = script.filter((s) => s.tab === ti && !s.heading);
    return (
      <p className="text-base leading-relaxed text-cream/85 md:text-lg">
        {lines.map((s) => {
          const idx = script.indexOf(s);
          const hot = idx === current;
          return (
            <span key={idx} data-testid={hot ? "case-sentence-active" : undefined} className={`rounded px-0.5 transition-colors duration-300 ${hot ? "bg-gold/20 text-cream" : ""}`}>
              {s.text}{" "}
            </span>
          );
        })}
      </p>
    );
  };

  return (
    <AnimatePresence>
      {open && c && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={close} className="fixed inset-0 z-[47] bg-black/70 backdrop-blur-sm" />
          <div className="pointer-events-none fixed inset-0 z-[48] flex items-center justify-center md:p-6">
          <motion.div
            data-testid="case-modal"
            role="dialog" aria-modal="true" aria-label={c.title}
            initial={{ opacity: 0, y: 30, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="pointer-events-auto flex h-full w-full flex-col bg-charcoal md:h-auto md:max-h-[88vh] md:w-[min(92vw,760px)] md:rounded-2xl md:border md:border-gold/35"
          >
            <header className="flex items-start justify-between gap-4 border-b border-cream/8 px-6 pb-5 pt-6 sm:px-8">
              <div>
                <span className="stamp inline-block">{c.badge}</span>
                <h3 className="mt-4 font-serif text-3xl leading-tight text-cream sm:text-4xl">{c.title}</h3>
                <p className="mt-2 text-sm italic text-gold/90">{c.hook}</p>
              </div>
              <button data-testid="case-close" onClick={close} aria-label="Close" className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-cream/10 text-gray2 hover:text-cream"><X className="h-5 w-5" /></button>
            </header>

            <div className="flex gap-1 overflow-x-auto border-b border-cream/8 px-6 sm:px-8" role="tablist">
              {TABS.map((t, i) => (
                <button key={t.key} role="tab" aria-selected={tab === i} data-testid={`case-tab-${t.key}`} onClick={() => setTab(i)}
                  className={`relative shrink-0 px-3 py-3 text-sm transition-colors ${tab === i ? "text-cream" : "text-gray2 hover:text-cream/80"}`}>
                  {t.label}
                  {tab === i && <motion.span layoutId="case-tab-underline" className="absolute inset-x-3 -bottom-px h-px bg-gold" />}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-7 sm:px-8">
              <AnimatePresence mode="wait">
                <motion.div key={tab} data-testid="case-tab-panel" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.25 }}>
                  {renderTab(tab)}
                </motion.div>
              </AnimatePresence>
              <div className="mt-7 flex flex-wrap gap-2">
                {c.chips.map((ch) => <span key={ch} className="chip">{ch}</span>)}
              </div>
            </div>

            <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-cream/8 px-6 py-4 sm:px-8">
              <div className="flex items-center gap-2">
                <button data-testid="case-listen" onClick={reading === "playing" ? pause : play} className="btn btn-red !px-4 !py-2 text-xs">
                  {reading === "playing" ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                  {reading === "playing" ? "Pause" : reading === "paused" ? "Resume" : "Listen"}
                </button>
                {reading !== "idle" && (
                  <button data-testid="case-stop" onClick={stop} aria-label="Stop" className="grid h-9 w-9 place-items-center rounded-full border border-cream/10 text-gray2 hover:text-cream"><Square className="h-3 w-3 fill-current" /></button>
                )}
                <div className="ml-1 flex items-center gap-1 rounded-full border border-cream/10 p-0.5">
                  {RATES.map((r) => (
                    <button key={r} data-testid={`case-rate-${r}`} onClick={() => changeRate(r)} aria-pressed={rate === r}
                      className={`rounded-full px-2 py-1 font-mono text-[0.62rem] ${rate === r ? "bg-gold/15 text-gold" : "text-gray2 hover:text-cream"}`}>{r}×</button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="case-prev" onClick={() => openCase(caseIndex - 1)} aria-label="Previous case" className="grid h-9 w-9 place-items-center rounded-full border border-cream/10 text-gray2 hover:border-gold hover:text-gold"><ChevronLeft className="h-4 w-4" /></button>
                <span className="font-mono text-[0.62rem] tracking-widest text-gray2">{caseIndex + 1} / {cases.items.length}</span>
                <button data-testid="case-next" onClick={() => openCase(caseIndex + 1)} aria-label="Next case" className="grid h-9 w-9 place-items-center rounded-full border border-cream/10 text-gray2 hover:border-gold hover:text-gold"><ChevronRight className="h-4 w-4" /></button>
              </div>
            </footer>
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
